import { cleanProductName, formatUtcDateTimeForInput } from '../../utils/helper';

export const getByPath = (source = {}, path = '') =>
    String(path || '')
        .split('.')
        .reduce((current, key) => (current && current[key] !== undefined ? current[key] : undefined), source);

export const getFirstValue = (source = {}, paths = [], fallback = '') => {
    for (const path of paths) {
        const value = getByPath(source, path);
        if (value !== undefined && value !== null && value !== '') return value;
    }

    return fallback;
};

export const getFirstArrayValue = (source = {}, paths = []) => {
    for (const path of paths) {
        const value = getByPath(source, path);
        if (Array.isArray(value)) return value;
    }

    return [];
};

export const normalizeNumber = (value) => {
    const parsed = Number(String(value ?? '').replace(/,/g, '').trim());
    return Number.isFinite(parsed) ? parsed : null;
};

export const formatValue = (value, fallback = '--') => {
    if (value === undefined || value === null || value === '') return fallback;
    return String(value);
};

export const formatNumberValue = (value, fallback = '--') => {
    const parsed = normalizeNumber(value);
    if (parsed === null) return fallback;
    return parsed.toLocaleString();
};

export const formatCurrencyValue = (value, fallback = '--') => {
    const formattedValue = formatNumberValue(value, fallback);
    return formattedValue === fallback ? fallback : `PKR ${formattedValue}`;
};

export const formatDistanceValue = (value, fallback = '--') => {
    if (value === undefined || value === null || value === '') return fallback;
    const parsed = normalizeNumber(value);
    if (parsed === null) return String(value);
    return `${parsed.toLocaleString()} km`;
};

export const formatDateTime = (value, fallback = '--') => {
    if (!value) return fallback;

    const parsedDate = new Date(value);
    if (Number.isNaN(parsedDate.getTime())) return String(value);

    return parsedDate.toLocaleString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
    }).replace(',', '').replace(/\b(am|pm)\b/i, (match) => match.toUpperCase());
};

export const formatDateOnly = (value, fallback = '--') => {
    if (!value) return fallback;

    const parsedDate = new Date(value);
    if (Number.isNaN(parsedDate.getTime())) return String(value);

    return parsedDate.toLocaleDateString('en-GB').replace(/\//g, '-');
};

export const stripHtml = (value) =>
    String(value || '')
        .replace(/<[^>]*>/g, ' ')
        .replace(/&nbsp;/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

export const toInputValue = (value) => (
    value === undefined || value === null ? '' : String(value)
);

export const getRealTripId = (id) => {
    if (!id) return null;
    return String(id).replace(/^TR-/i, '');
};

export const getTripIdDisplay = (detail = {}, routeId = '') =>
    getFirstValue(detail, [
        'trip_id',
        'tripId',
        'trip_number',
        'tripNumber',
        'reference_no',
        'reference_number',
    ], routeId || '--');

export const formatPhone = (value, fallback = '--') => {
    const phone = String(value || '').trim();
    if (!phone) return fallback;
    return phone.startsWith('+') ? phone : `+${phone}`;
};

export const getTripCustomerId = (detail = {}) => {
    const customer = detail?.customer;

    return detail?.customer_id ||
        detail?.data?.customer_id ||
        detail?.customerId ||
        detail?.data?.customerId ||
        detail?.['3pl_customer_id'] ||
        detail?.data?.['3pl_customer_id'] ||
        detail?.customer_info?.id ||
        detail?.data?.customer_info?.id ||
        detail?.customer_detail?.id ||
        detail?.data?.customer_detail?.id ||
        detail?.customer?.id ||
        detail?.customer?.customer_id ||
        detail?.customer?.customerId ||
        detail?.customer?.value ||
        detail?.data?.customer?.id ||
        detail?.data?.customer?.customer_id ||
        detail?.data?.customer?.customerId ||
        detail?.data?.customer?.value ||
        (typeof customer === 'number' || (typeof customer === 'string' && !Number.isNaN(Number(customer))) ? customer : null);
};

export const getTripCustomerName = (detail = {}, fallback = '--') => {
    const customer = detail?.customer || detail?.customer_info || detail?.customer_detail || detail?.three_pl_customer || detail?.['3pl_customer'];

    if (customer && typeof customer === 'object') {
        const customerName = customer?.name ||
            customer?.label ||
            customer?.title ||
            customer?.customer_name ||
            customer?.customerName ||
            customer?.company_name ||
            customer?.companyName ||
            customer?.display_name ||
            customer?.displayName ||
            customer?.customer?.name ||
            customer?.customer?.label ||
            customer?.customer?.customer_name;
        if (customerName) return customerName;
    }

    if (typeof customer === 'string' && Number.isNaN(Number(customer))) {
        return customer;
    }

    return getFirstValue(detail, [
        'customer_name',
        'customerName',
        'customer_label',
        'customer_company_name',
        'company_name',
        '3pl_customer_name',
        'three_pl_customer.name',
        '3pl_customer.name',
        'data.customer_name',
        'data.customerName',
        'data.customer.name',
        'data.customer.label',
        'data.customer.customer_name',
        'data.customer.company_name',
        'data.customer_info.name',
        'data.customer_detail.name',
    ], fallback);
};

export const getCustomerAddress = (detail = {}, fallback = '--') => {
    const customer = detail?.customer || detail?.customer_info || detail?.customer_detail || {};

    return getFirstValue({
        ...detail,
        customer,
    }, [
        'billing_address',
        'invoice_address',
        'customer.billing_address',
        'customer.invoice_address',
        'customer.address',
        'customer.location',
        'customer.customer_address',
        'customer_detail.address',
        'customer_info.address',
    ], fallback);
};

export const getVehicleLabel = (detail = {}, fallback = '--') => {
    const vehicle = detail?.vehicle || {};
    const registration = vehicle?.registration_number ||
        vehicle?.reg_no ||
        vehicle?.vehicle_number ||
        vehicle?.plate_number ||
        detail?.vehicle_registration_number ||
        detail?.vehicle_number ||
        detail?.vehicle_id;
    const type = detail?.vehicle_type?.name ||
        detail?.vehicle_type_name ||
        vehicle?.vehicle_type?.name ||
        vehicle?.vehicle_type_name ||
        vehicle?.type ||
        vehicle?.vehicle_type;

    return [registration || fallback, type ? `(${type})` : ''].filter(Boolean).join(' ');
};

export const getDriverName = (detail = {}, fallback = '--') =>
    getFirstValue(detail, [
        'primary_driver_name',
        'primary_driver.name',
        'primary_driver.username',
        'driver_name',
        'driver.name',
        'driver.username',
    ], fallback);

export const getDriverPhone = (detail = {}, fallback = '--') =>
    formatPhone(getFirstValue(detail, [
        'primary_driver_contact',
        'primary_driver.phone',
        'primary_driver.contact',
        'driver_contact',
        'driver.phone',
        'driver.contact',
    ]), fallback);

export const getDriverImage = (detail = {}) =>
    getFirstValue(detail, [
        'primary_driver.image',
        'primary_driver.avatar',
        'driver.image',
        'driver.avatar',
    ], '');

const getRelationId = (detail = {}, relationKey = '', paths = []) => {
    const relation = detail?.[relationKey] ?? detail?.data?.[relationKey];

    if (typeof relation === 'number' || (typeof relation === 'string' && relation.trim() !== '' && !Number.isNaN(Number(relation)))) {
        return String(relation).trim();
    }

    return String(getFirstValue(detail, paths, '')).trim();
};

export const getTripContractId = (detail = {}) =>
    getRelationId(detail, 'contract', [
        'contract_id',
        'contractId',
        'contract.id',
        'contract.contract_id',
        'contract.value',
        'data.contract_id',
        'data.contractId',
        'data.contract.id',
        'data.contract.contract_id',
        'data.contract.value',
    ]);

export const getTripCostCenterId = (detail = {}) =>
    getRelationId(detail, 'cost_center', [
        'cost_center_id',
        'costCenterId',
        'cost_center.id',
        'cost_center.cost_center_id',
        'cost_center.value',
        'data.cost_center_id',
        'data.costCenterId',
        'data.cost_center.id',
        'data.cost_center.cost_center_id',
        'data.cost_center.value',
    ]);

export const getTripContractName = (detail = {}, fallback = '') =>
    getFirstValue(detail, [
        'contract.name',
        'contract.label',
        'contract.title',
        'contract.contract_name',
        'contract.contract_no',
        'contract.contract_number',
        'contract_name',
        'contractName',
        'contract_label',
        'data.contract.name',
        'data.contract.label',
        'data.contract.title',
        'data.contract_name',
    ], fallback);

export const getTripCostCenterName = (detail = {}, fallback = '') =>
    getFirstValue(detail, [
        'cost_center.name',
        'cost_center.label',
        'cost_center.title',
        'cost_center.cost_center_name',
        'cost_center.code',
        'cost_center_name',
        'costCenterName',
        'cost_center_label',
        'data.cost_center.name',
        'data.cost_center.label',
        'data.cost_center.title',
        'data.cost_center_name',
    ], fallback);

export const joinContractCostCenter = (contractLabel, costCenterLabel, fallback = '--') =>
    [contractLabel, costCenterLabel]
        .map((value) => String(value ?? '').trim())
        .filter(Boolean)
        .join(' - ') || fallback;

export const getContractCostCenterLabel = (detail = {}, fallback = '--') =>
    joinContractCostCenter(getTripContractName(detail), getTripCostCenterName(detail), fallback);

export const buildTripMetaProps = (detail = {}) => ({
    customerName: getTripCustomerName(detail),
    vehicleLabel: getVehicleLabel(detail),
    driverName: getDriverName(detail),
    driverContact: getDriverPhone(detail),
    driverImage: getDriverImage(detail),
    contractCostCenter: getContractCostCenterLabel(detail),
});

const getStopLocationId = (stop = {}) =>
    String(
        getFirstValue(stop, [
            'location_id',
            'customer_warehouse_id',
            'warehouse_id',
            'location.id',
            'location.value',
            'location.customer_warehouse_id',
            'location.warehouse_id',
            'location.warehouse.id',
            'location.warehouse.value',
            'customer_warehouse.id',
            'customer_warehouse.value',
            'customer_warehouse.customer_warehouse_id',
            'customer_warehouse.warehouse_id',
            'customer_warehouse.warehouse.id',
            'customer_warehouse.warehouse.value',
            'warehouse.id',
            'warehouse.value',
        ])
    ).trim();

const getCustomerLocationName = (stop = {}, customerLocations = []) => {
    const stopLocationId = getStopLocationId(stop);
    if (!stopLocationId) return '';

    const matchedLocation = (Array.isArray(customerLocations) ? customerLocations : []).find((location) => {
        const ids = [
            location?.value,
            location?.id,
            location?.customer_warehouse_id,
            location?.warehouse_id,
            location?.raw?.id,
            location?.raw?.value,
            location?.raw?.customer_warehouse_id,
            location?.raw?.warehouse_id,
            location?.raw?.warehouse?.id,
            location?.raw?.warehouse?.value,
        ].map((value) => String(value ?? '').trim()).filter(Boolean);

        return ids.includes(stopLocationId);
    });

    return matchedLocation?.label || matchedLocation?.name || '';
};

const buildLocationNameWithContext = (source = {}) => {
    const name = getFirstValue(source, [
        'warehouse_name',
        'name',
        'label',
        'title',
        'location_name',
        'point_name',
        'warehouse.warehouse_name',
        'warehouse.name',
        'warehouse.label',
    ]);
    const city = getFirstValue(source, ['city', 'warehouse.city', 'location.city']);
    const address = getFirstValue(source, ['address', 'location_point', 'warehouse.address']);
    const extra = city || (address && address !== name ? address : '');

    return [name, extra].filter(Boolean).join(' - ');
};

export const getStopLocationLabel = (stop = {}, customerLocations = []) => {
    const customerLocationName = getCustomerLocationName(stop, customerLocations);
    if (customerLocationName) return customerLocationName;

    const rawLocationObject = stop?.location && typeof stop.location === 'object' ? stop.location : null;
    const nestedLocationName = rawLocationObject ? buildLocationNameWithContext(rawLocationObject) : '';

    if (nestedLocationName) return nestedLocationName;

    const locationName = getFirstValue(stop, [
        'location.warehouse_name',
        'location.name',
        'location.label',
        'location.title',
        'location.location_name',
        'location.point_name',
        'location.warehouse.warehouse_name',
        'location.warehouse.name',
        'location.warehouse.label',
        'location.warehouse.title',
        'customer_warehouse.warehouse_name',
        'customer_warehouse.name',
        'customer_warehouse.label',
        'customer_warehouse.title',
        'customer_warehouse.location_name',
        'customer_warehouse.warehouse.warehouse_name',
        'customer_warehouse.warehouse.name',
        'customer_warehouse.warehouse.label',
        'customer_warehouse.warehouse.title',
        'warehouse.warehouse_name',
        'warehouse.name',
        'warehouse.label',
        'warehouse.title',
        'warehouse_title',
        'warehouse_name',
        'location_name',
        'point_name',
    ]);

    if (locationName) return String(locationName).trim();

    const location = stop?.location;
    if (typeof location === 'string' && location.trim()) return location.trim();

    return formatValue(
        getFirstValue(stop, [
            'dispatch_point',
            'decanting_point',
            'start_location',
            'end_location',
            'location_point',
            'location.location_point',
            'location.location',
            'customer_warehouse.address',
            'customer_warehouse.warehouse.address',
            'warehouse.address',
            'address',
        ]),
        '--'
    );
};

export const getTripStops = (detail = {}) => {
    const rawStops = detail?.trip_stops || detail?.stops || detail?.tripStops || [];
    const sortedStops = (Array.isArray(rawStops) ? [...rawStops] : [])
        .sort((a, b) => (Number(a?.stop_number) || 0) - (Number(b?.stop_number) || 0));

    if (sortedStops.length > 0) return sortedStops;

    const startLocation = getFirstValue(detail, [
        'dispatch_point',
        'start_location',
        'startLocation',
        'origin',
        'from_location',
        'fromLocation',
        'loading_point',
        'pickup_location',
        'pickupLocation',
    ]);
    const endLocation = getFirstValue(detail, [
        'decanting_point',
        'end_location',
        'endLocation',
        'destination',
        'to_location',
        'toLocation',
        'unloading_point',
        'dropoff_location',
        'dropoffLocation',
    ]);

    return [
        startLocation ? { id: 'detail-start-location', stop_number: 1, stop_type: 'load', location: startLocation } : null,
        endLocation ? { id: 'detail-end-location', stop_number: 2, stop_type: 'unload', location: endLocation } : null,
    ].filter(Boolean);
};

const getStopActions = (stop = {}, index = 0, total = 0) => {
    const rawAction = String(stop?.stop_type || stop?.stopType || stop?.action || stop?.type || '').trim().toLowerCase();
    const actions = [];

    if (rawAction.includes('load') || rawAction.includes('pickup') || rawAction.includes('dispatch')) {
        actions.push('Load');
    }
    if (rawAction.includes('unload') || rawAction.includes('decant') || rawAction.includes('deliver') || rawAction.includes('drop')) {
        actions.push('Unload');
    }
    if (!actions.length && index === 0) actions.push('Load');
    if (!actions.length && index === total - 1) actions.push('Unload');

    return [...new Set(actions)];
};

const getStopDetails = (stop = {}) => {
    const parts = [
        formatDateTime(getFirstValue(stop, [
            'actual_at',
            'actual_arrival_at',
            'eta',
            'expected_at',
            'expected_arrival_at',
            'start_at',
            'created_at',
        ]), ''),
        getFirstValue(stop, ['quantity', 'qty', 'loaded_quantity', 'unloaded_quantity'])
            ? `${formatNumberValue(getFirstValue(stop, ['quantity', 'qty', 'loaded_quantity', 'unloaded_quantity']))} qty`
            : '',
        cleanProductName(getFirstValue(stop, ['product.name', 'product.label', 'product_name', 'material_name'])),
    ].filter(Boolean);

    return parts.join(' - ') || '--';
};

export const buildRouteJourneyStops = (detail = {}, customerLocations = []) => {
    const stops = getTripStops(detail);

    return stops.map((stop, index) => {
        const isFirst = index === 0;
        const isLast = index === stops.length - 1;
        const type = isFirst ? 'start' : isLast ? 'end' : 'stop';

        return {
            id: stop?.id || stop?.trip_stop_id || `${type}-${index}`,
            type,
            number: String(index),
            badge: isFirst ? 'Trip Start' : isLast ? 'Trip End' : `Stop-${index}`,
            actions: getStopActions(stop, index, stops.length),
            location: getStopLocationLabel(stop, customerLocations),
            meta: getStopDetails(stop),
        };
    });
};

export const TRIP_DISTANCE_PATHS = [
    'covered_distance',
    'total_distance',
    'distance',
    'trip_distance',
    'route_distance',
    'distance_km',
];

// A trip that has not moved yet reports covered_distance as 0, so plain getFirstValue would
// stop there and never reach the planned total_distance. Prefer the first non-zero reading.
export const getTripDistanceValue = (detail = {}, paths = TRIP_DISTANCE_PATHS) => {
    for (const path of paths) {
        const parsed = normalizeNumber(getByPath(detail, path));
        if (parsed !== null && parsed > 0) return parsed;
    }

    return getFirstValue(detail, paths);
};

export const getTripDistanceLabel = (detail = {}) => formatDistanceValue(getTripDistanceValue(detail));

export const getRouteStopCount = (stops = []) => (Array.isArray(stops) ? stops : []).length;

export const getRouteJourneySummary = (detail = {}, stops = []) => {
    const distanceLabel = getTripDistanceLabel(detail);
    const stopCount = getRouteStopCount(stops);

    if (stopCount === 0) return distanceLabel;

    return `${distanceLabel} · ${stopCount} ${stopCount === 1 ? 'stop' : 'stops'}`;
};

export const getChecklistRows = (detail = {}, paths = []) => getFirstArrayValue(detail, paths);

const normalizeStatus = (status) => String(status || '').trim().toLowerCase();

export const getChecklistSummary = (rows = []) => {
    const safeRows = Array.isArray(rows) ? rows : [];
    const total = safeRows.length;
    const passed = safeRows.filter((row) => {
        const status = normalizeStatus(row?.status || row?.answer);
        return ['pass', 'passed', 'complete', 'completed', 'agree', 'approved', 'yes', 'na', 'n/a'].includes(status);
    }).length;
    const failed = safeRows.filter((row) => {
        const status = normalizeStatus(row?.status || row?.answer);
        return ['fail', 'failed', 'disagree', 'no', 'rejected'].includes(status);
    }).length;
    const pending = total ? Math.max(total - passed - failed, 0) : 0;

    return { total, passed, failed, pending };
};

export const getAttachmentCount = (item = {}) =>
    getFirstArrayValue(item, ['files', 'images', 'attachments', 'documents', 'gallery_files', 'media']).length;

const getAttachment = (item = {}) => {
    const attachments = getFirstArrayValue(item, ['files', 'images', 'attachments', 'documents', 'gallery_files', 'media']);
    const first = attachments[0];

    if (!first) return null;
    if (typeof first === 'string') {
        return {
            name: first.split('/').pop() || 'Attachment',
            url: first,
        };
    }

    const url = first?.url || first?.photo_url || first?.file_url || first?.file_path || first?.path || '';
    return {
        name: first?.fileName || first?.file_name || first?.name || first?.title || (url ? url.split('/').pop() : 'Attachment'),
        url,
    };
};

const getFuelLogLocation = (log = {}) => {
    const station = log?.fuel_station || log?.station || log?.vendor || {};
    const stationName = station?.fuel_station_name || station?.name || station?.label || station?.title;
    const stationLocation = station?.location || station?.address;

    return [stationName, stationLocation].filter(Boolean).join(', ') ||
        getFirstValue(log, [
            'fuel_station_name',
            'station_name',
            'vendor_name',
            'location',
            'address',
            'source',
        ], '--');
};

export const getFuelLogs = (detail = {}) => {
    const rows = getFirstArrayValue(detail, [
        'fuel_logs',
        'fuelLogs',
        'trip_fuel_logs',
        'tripFuelLogs',
        'refueling_history',
        'fuel_records',
        'fuels',
    ]);

    return rows.map((log, index) => {
        const dateValue = getFirstValue(log, ['refuel_at', 'filled_at', 'date', 'created_at', 'updated_at']);
        const liters = getFirstValue(log, ['claimed_fueled', 'fuel_quantity', 'quantity', 'litres', 'liters', 'ltrs', 'filled_fuel']);
        const cost = getFirstValue(log, ['total_fuel_cost', 'expected_total_fuel_cost', 'total_amount', 'amount', 'cost']);

        const files = getFirstArrayValue(log, ['files', 'images', 'attachments', 'documents', 'gallery_files', 'media']);
        const attachment = getAttachment(log);
        const resolvedFiles = files.length > 0 ? files : (attachment ? [attachment] : []);
        const attachmentsCount = files.length || (attachment ? 1 : 0);

        return {
            id: log?.id || log?.fuel_log_id || index,
            date: formatDateTime(dateValue),
            displayDate: formatDateOnly(dateValue),
            location: getFuelLogLocation(log),
            tableLocation: getFuelLogLocation(log),
            liters: formatNumberValue(liters),
            cost: formatNumberValue(cost),
            rawLiters: normalizeNumber(liters) || 0,
            rawCost: normalizeNumber(cost) || 0,
            attachments: attachmentsCount,
            attachment,
            files: resolvedFiles,
            raw: log,
        };
    });
};

const getExpenseTypeLabel = (expense = {}) =>
    getFirstValue(expense, [
        'expense_category.title',
        'expense_category.name',
        'expense_category.label',
        'expense_category_title',
        'expenseCategory.title',
        'expenseCategory.name',
        'expenseCategory.label',
        'category.title',
        'category.name',
        'category.label',
        'category_title',
        'expense_type.title',
        'expense_type.name',
        'expense_type.label',
        'expense_type',
        'type',
        'title',
        'name',
        'description',
    ], '--');

export const getExpenseLogs = (detail = {}) => {
    const rows = getFirstArrayValue(detail, [
        'trip_expenses',
        'tripExpenses',
        'expense_logs',
        'expenses',
        'trip_expense_logs',
    ]);

    return rows.map((expense, index) => {
        const dateValue = getFirstValue(expense, ['expense_at', 'date', 'created_at', 'updated_at']);
        const cost = getFirstValue(expense, ['amount', 'cost', 'total_amount', 'expense_amount']);
        const files = getFirstArrayValue(expense, ['files', 'images', 'attachments', 'documents', 'gallery_files', 'media']);
        const attachment = getAttachment(expense);
        const resolvedFiles = files.length > 0 ? files : (attachment ? [attachment] : []);
        const attachmentsCount = files.length || (attachment ? 1 : 0);

        return {
            id: expense?.id || expense?.trip_expense_id || index,
            date: formatDateTime(dateValue),
            displayDate: formatDateOnly(dateValue),
            type: getExpenseTypeLabel(expense),
            cost: formatNumberValue(cost),
            cardCost: formatCurrencyValue(cost),
            rawCost: normalizeNumber(cost) || 0,
            attachments: attachmentsCount,
            attachment,
            files: resolvedFiles,
            raw: expense,
        };
    });
};

export const sumAmount = (rows = [], paths = ['amount']) =>
    (Array.isArray(rows) ? rows : []).reduce((sum, row) => {
        const amount = getFirstValue(row, paths, 0);
        return sum + (normalizeNumber(amount) || 0);
    }, 0);

export const getPercentText = (part, total) => {
    const parsedPart = normalizeNumber(part);
    const parsedTotal = normalizeNumber(total);
    if (parsedPart === null || parsedTotal === null || parsedTotal <= 0) return '--';
    return `${Math.round((parsedPart / parsedTotal) * 100)}%`;
};

export const buildTripMetrics = (detail = {}) => {
    const fuelLogs = getFuelLogs(detail);
    const expenseLogs = getExpenseLogs(detail);
    const tripPettyCash = getFirstArrayValue(detail, ['trip_petty_cash', 'tripPettyCash', 'petty_cash_logs', 'pettyCash']);
    const allocatedPettyCash = getFirstValue(detail, ['allocated_petty_cash', 'allocatedPettyCash']);
    const totalPettyCashAmount = sumAmount(tripPettyCash, ['amount', 'total_amount', 'petty_cash_amount']);
    const totalPettyCashDisplayValue = normalizeNumber(allocatedPettyCash) !== null ? allocatedPettyCash : totalPettyCashAmount;
    const totalExpenseAmount = getFirstValue(detail, [
        'total_expense_cost',
        'total_expenses',
        'expense_cost',
        'totalTripExpense',
    ], expenseLogs.reduce((sum, log) => sum + log.rawCost, 0));
    const fuelCost = getFirstValue(detail, [
        'fuelCost',
        'fuel_cost',
        'total_fuel_cost',
        'totalTripFuelCost',
    ], fuelLogs.reduce((sum, log) => sum + log.rawCost, 0));
    const totalFilledFuel = getFirstValue(detail, [
        'total_filled_fuel',
        'filled_fuel',
        'filledFuel',
    ], fuelLogs.reduce((sum, log) => sum + log.rawLiters, 0));
    const remainingPettyCashAmount = Math.max((normalizeNumber(totalPettyCashDisplayValue) || 0) - (normalizeNumber(totalExpenseAmount) || 0), 0);
    const totalSpendAmount = getFirstValue(detail, [
        'total_spend',
        'totalSpend',
        'total_cost',
        'trip_total_cost',
    ], (normalizeNumber(fuelCost) || 0) + (normalizeNumber(totalExpenseAmount) || 0));
    const fuelRate = getFirstValue(detail, [
        'fuel_rate',
        'fuelRate',
        'current_fuel_rate',
        'expected_rate',
    ], normalizeNumber(totalFilledFuel) ? (normalizeNumber(fuelCost) || 0) / normalizeNumber(totalFilledFuel) : '');

    return {
        fuelLogs,
        expenseLogs,
        tripPettyCash,
        allocatedPettyCash,
        totalPettyCashAmount,
        totalPettyCashDisplayValue,
        totalExpenseAmount,
        fuelCost,
        totalFilledFuel,
        remainingPettyCashAmount,
        totalSpendAmount,
        fuelRate,
    };
};

export const getTripNotes = (detail = {}) =>
    getFirstValue(detail, [
        'general_notes',
        'closing_notes',
        'completed_notes',
        'in_transit_notes',
        'loading_dispatch_notes',
        'dispatch_notes',
        'notes',
        'note',
        'remarks',
    ], '');

const getReviewNoteUser = (entry = {}) =>
    entry?.user || entry?.created_by || entry?.approved_by || entry?.reviewed_by || entry?.actioned_by || {};

// Relation fields sometimes come back as objects; never hand one straight to JSX.
const toReviewNoteText = (value, fallback = '') =>
    (value === null || value === undefined || typeof value === 'object') ? fallback : String(value).trim() || fallback;

const buildReviewNote = (entry = {}, index = 0) => {
    const user = getReviewNoteUser(entry);
    const text = stripHtml(getFirstValue(entry, [
        'remarks',
        'remark',
        'comment',
        'comments',
        'note',
        'notes',
        'description',
        'message',
    ]));

    if (!text) return null;

    return {
        id: entry?.id || entry?.approval_id || `review-note-${index}`,
        name: toReviewNoteText(getFirstValue({ ...entry, user }, [
            'user.name',
            'user.username',
            'user.full_name',
            'name',
            'username',
            'created_by_name',
            'approved_by_name',
        ]), '--'),
        role: toReviewNoteText(getFirstValue({ ...entry, user }, [
            'user.role.name',
            'user.role',
            'user.designation',
            'role.name',
            'role',
            'designation',
        ])),
        date: formatDateOnly(getFirstValue(entry, [
            'approved_at',
            'reviewed_at',
            'actioned_at',
            'created_at',
            'updated_at',
        ]), ''),
        text,
    };
};

/**
 * Review remarks shown on the Verification / Approval summary.
 * Prefers a structured approval trail from the detail API and falls back to the trip's
 * own closing remarks so the screen never renders placeholder people.
 */
export const getTripReviewNotes = (detail = {}) => {
    const trailEntries = getFirstArrayValue(detail, [
        'trip_approvals',
        'approvals',
        'approval_trail',
        'approval_logs',
        'trip_approval_logs',
        'verification_logs',
        'review_notes',
        'trip_remarks',
        'data.trip_approvals',
        'data.approvals',
    ]);

    const notes = trailEntries
        .map((entry, index) => buildReviewNote(entry, index))
        .filter(Boolean);

    if (notes.length > 0) return notes;

    const closingRemarks = stripHtml(getFirstValue(detail, ['closing_remarks', 'close_remarks', 'remarks']));
    if (!closingRemarks) return [];

    const closedBy = detail?.closed_by || detail?.updated_by || detail?.closer || {};

    return [{
        id: 'closing-remarks',
        name: toReviewNoteText(getFirstValue({ ...detail, closedBy }, [
            'closedBy.name',
            'closedBy.username',
            'closedBy.full_name',
            'closed_by_name',
        ]), '--'),
        role: toReviewNoteText(getFirstValue({ ...detail, closedBy }, [
            'closedBy.role.name',
            'closedBy.role',
            'closedBy.designation',
        ])),
        date: formatDateOnly(getFirstValue(detail, ['closed_at', 'actual_end_at', 'completed_at', 'updated_at']), ''),
        text: closingRemarks,
    }];
};

export const buildClosingInitialValues = (detail = {}) => ({
    actual_start_datetime: formatUtcDateTimeForInput(getFirstValue(detail, [
        'actual_start_at',
        'started_at',
        'dispatch_at',
        'start_at',
        'created_at',
    ])),
    actual_end_datetime: formatUtcDateTimeForInput(getFirstValue(detail, [
        'actual_end_at',
        'completed_at',
        'ended_at',
        'end_at',
        'updated_at',
    ])),
    start_odometer: toInputValue(getFirstValue(detail, ['start_odm', 'start_odometer', 'startOdometer'])),
    closing_odometer: toInputValue(getFirstValue(detail, ['end_odm', 'closing_odometer', 'endOdometer', 'closingOdometer'])),
    opening_fuel: toInputValue(getFirstValue(detail, ['opening_fuel', 'openingFuel'])),
    closing_fuel: toInputValue(getFirstValue(detail, ['closing_fuel', 'closingFuel'])),
    dispatched: toInputValue(getFirstValue(detail, [
        'dispatched',
        'dispatched_qty',
        'dispatch_qty',
        'loaded_quantity',
        'loaded_qty',
        'total_dispatched_quantity',
        'quantity',
    ])),
    delivered: toInputValue(getFirstValue(detail, [
        'delivered',
        'delivered_qty',
        'delivery_qty',
        'unloaded_quantity',
        'unloaded_qty',
        'total_delivered_quantity',
    ])),
    delivered_qty: toInputValue(getFirstValue(detail, [
        'delivered_qty',
        'delivered',
        'delivery_qty',
        'unloaded_quantity',
        'unloaded_qty',
        'total_delivered_quantity',
    ])),
    shortage: toInputValue(getFirstValue(detail, ['shortage', 'shortage_qty', 'shortage_quantity'])),
    relief_qty: toInputValue(getFirstValue(detail, ['relief_qty', 'relief_quantity', 'reliefQty'])),
    remarks: toInputValue(getFirstValue(detail, ['closing_remarks', 'close_remarks', 'remarks'])),
    invoice_remarks: toInputValue(getFirstValue(detail, ['invoice_remarks', 'invoice_notes'])),
    general_notes: toInputValue(getTripNotes(detail)),
});

export const getTripCompletionSummary = (detail = {}, metrics = buildTripMetrics(detail)) => {
    const routeStops = getTripStops(detail);
    const completedAt = getFirstValue(detail, [
        'actual_end_at',
        'completed_at',
        'ended_at',
        'end_at',
        'updated_at',
    ]);
    return {
        statusLabel: formatValue(getFirstValue(detail, ['status', 'trip_status'], 'Completed')),
        deliveredLabel: routeStops.length ? `${routeStops.length} stops delivered` : 'Trip completed',
        distanceLabel: getTripDistanceLabel(detail),
        closedAtLabel: formatDateTime(completedAt),
        totalSpendLabel: formatCurrencyValue(metrics.totalSpendAmount),
    };
};

const normalizeTripFile = (fileEntry = {}) => {
    if (typeof fileEntry === 'string') {
        const fileName = fileEntry.trim();
        return fileName ? {
            fileName,
            originalName: fileName.split('/').pop() || fileName,
            previewUrl: fileName,
            backend: true,
        } : null;
    }

    if (!fileEntry || typeof fileEntry !== 'object') return null;

    const fileName = getFirstValue(fileEntry, [
        'file_path',
        'fileName',
        'file_name',
        'file_url',
        'full_url',
        'media_url',
        'path',
        'url',
        'src',
        'document',
        'file',
        'image',
    ]);

    if (!fileName) return null;

    return {
        ...fileEntry,
        fileName,
        originalName: getFirstValue(fileEntry, ['originalName', 'original_name', 'name', 'title'], String(fileName).split('/').pop() || fileName),
        previewUrl: getFirstValue(fileEntry, ['previewUrl', 'url', 'file_url', 'full_url', 'media_url', 'path'], fileName),
        backend: true,
    };
};

export const getTripAttachments = (detail = {}) =>
    getFirstArrayValue(detail, [
        'attachments',
        'trip_attachments',
        'tripAttachments',
        'files',
        'trip_files',
        'documents',
        'gallery_files',
        'media',
    ]).map(normalizeTripFile).filter(Boolean);
